import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  birthDate?: string;
  weightKg?: number;
  heightCm?: number;
}

interface MedicalRecord {
  id?: string;
  hasPreviewHeartCondition?: boolean;
  isPregnant?: boolean;
  cardiovascularConditions: string[];
  metabolicConditions: string[];
  respiratoryConditions: string[];
  musculoskeletalConditions: string[];
  otherConditions: string[];
  thyroidConditions: string[];
  symptomsAndConditions: Record<string, boolean>;
  physicalActivities: string[];
  additionalNotes?: string;
  signatureData?: string;
  signatureDate?: string;
}

interface MedicalRecordPDFViewProps {
  client: Client;
  medicalRecord: MedicalRecord;
}

const MedicalRecordPDFView: React.FC<MedicalRecordPDFViewProps> = ({
  client,
  medicalRecord
}) => {
  const { t } = useTranslation();

  const symptomsQuestions = t('medicalRecord.symptomsAndConditions.questions', { returnObjects: true }) as Array<{
    key: string;
    label: string;
  }>;

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('medicalRecord.clientDetails.notRecorded');
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatYesNo = (value?: boolean) => {
    if (value === undefined || value === null) return t('medicalRecord.formatting.notAnswered');
    return value ? t('medicalRecord.basicQuestions.yes') : t('medicalRecord.basicQuestions.no');
  };

  return (
    <div className="p-8 bg-white text-black max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{t('medicalRecord.title')}</h1>
        <div className="h-0.5 bg-black w-full"></div>
      </div>

      {/* Client Details */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-4">{t('medicalRecord.clientDetails.title')}</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="font-semibold">{t('medicalRecord.clientDetails.name')}:</span> {client.firstName} {client.lastName}
          </div>
          <div>
            <span className="font-semibold">{t('medicalRecord.clientDetails.birthDate')}:</span> {formatDate(client.birthDate)}
          </div>
          <div>
            <span className="font-semibold">{t('medicalRecord.clientDetails.email')}:</span> {client.email || t('medicalRecord.clientDetails.notRecorded')}
          </div>
          <div>
            <span className="font-semibold">{t('medicalRecord.clientDetails.phone')}:</span> {client.phone || t('medicalRecord.clientDetails.notRecorded')}
          </div>
          <div>
            <span className="font-semibold">{t('medicalRecord.clientDetails.weight')}:</span> {client.weightKg ? `${client.weightKg} kg` : t('medicalRecord.clientDetails.notRecorded')}
          </div>
          <div>
            <span className="font-semibold">{t('medicalRecord.clientDetails.height')}:</span> {client.heightCm ? `${client.heightCm} cm` : t('medicalRecord.clientDetails.notRecorded')}
          </div>
        </div>
      </div>

      {/* Basic Questions */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-4">{t('medicalRecord.basicQuestions.title')}</h2>
        <div className="space-y-2">
          <div>
            <span className="font-semibold">{t('medicalRecord.basicQuestions.heartCondition')}:</span> {formatYesNo(medicalRecord.hasPreviewHeartCondition)}
          </div>
          <div>
            <span className="font-semibold">{t('medicalRecord.basicQuestions.pregnant')}:</span> {formatYesNo(medicalRecord.isPregnant)}
          </div>
        </div>
      </div>

      {/* Medical Conditions */}
      {medicalRecord.cardiovascularConditions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.cardiovascularConditions.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.cardiovascularConditions.map((condition) => (
              <li key={condition}>{condition}</li>
            ))}
          </ul>
        </div>
      )}

      {medicalRecord.otherConditions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.otherConditions.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.otherConditions.map((condition) => (
              <li key={condition}>{condition}</li>
            ))}
          </ul>
        </div>
      )}

      {medicalRecord.metabolicConditions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.metabolicConditions.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.metabolicConditions.map((condition) => (
              <li key={condition}>{condition}</li>
            ))}
          </ul>
        </div>
      )}

      {medicalRecord.respiratoryConditions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.respiratoryConditions.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.respiratoryConditions.map((condition) => (
              <li key={condition}>{condition}</li>
            ))}
          </ul>
        </div>
      )}

      {medicalRecord.musculoskeletalConditions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.musculoskeletalConditions.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.musculoskeletalConditions.map((condition) => (
              <li key={condition}>{condition}</li>
            ))}
          </ul>
        </div>
      )}

      {medicalRecord.thyroidConditions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.thyroidConditions.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.thyroidConditions.map((condition) => (
              <li key={condition}>{condition}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Symptoms */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-4">{t('medicalRecord.symptomsAndConditions.title')}</h2>
        <div className="space-y-2">
          {symptomsQuestions.map((question, index) => (
            <div key={question.key}>
              <span className="font-semibold">{index + 1}. {question.label}</span>
              <br />
              <span className="ml-4">{t('medicalRecord.formatting.answer')}: {formatYesNo(medicalRecord.symptomsAndConditions[question.key])}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Physical Activities */}
      {medicalRecord.physicalActivities.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.physicalActivities.title')}</h2>
          <ul className="list-disc pl-6 space-y-1">
            {medicalRecord.physicalActivities.map((activity) => (
              <li key={activity}>{activity}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Additional Notes */}
      {medicalRecord.additionalNotes && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.additionalNotes.title')}</h2>
          <p className="whitespace-pre-wrap">{medicalRecord.additionalNotes}</p>
        </div>
      )}


      {/* Signature */}
      {medicalRecord.signatureData && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4">{t('medicalRecord.formatting.signature')}</h2>
          <div className="border border-gray-300 bg-black p-4">
            <img 
              src={medicalRecord.signatureData} 
              alt={t('medicalRecord.formatting.signature')} 
              className="max-h-24 mx-auto"
            />
            {medicalRecord.signatureDate && (
              <p className="text-center mt-2 text-sm">
                {t('medicalRecord.formatting.signatureDate')}: {formatDate(medicalRecord.signatureDate)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-300 text-sm text-gray-600 text-center">
        <p>{t('medicalRecord.footer.generated')} {format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
      </div>
    </div>
  );
};

export default MedicalRecordPDFView;